import React from 'react';
import EditableBlock from '@/portal/components/EditableBlock.tsx';
import {
  loadNotificationSubscriptions,
  updateNotificationSubscriptions,
  loadUsers,
  NOTIFICATION_TYPES,
  CHANNELS,
} from '@/portal/api';
import { UserData } from '@/portal/types/UserData.ts';
import NotificationSettingsView from './NotificationSettingsView.tsx';
import NotificationSettingsEdit, {
  NotificationSettingsFormData,
} from './NotificationSettingsEdit.tsx';

// For now, we'll focus on patient_referral notifications via email
// This can be expanded later for other notification types and channels
interface NotificationSettingsData {
  patientReferralEmailUsers: UserData[];
}

const NotificationSettingsBlock: React.FC = () => {
  const fetchData = async (): Promise<NotificationSettingsData> => {
    const [subscriptions, allUsers] = await Promise.all([
      loadNotificationSubscriptions(),
      loadUsers(),
    ]);

    // Find the patient_referral email subscriptions
    const patientReferralEmailGroup = subscriptions.find(
      (group) =>
        group.notificationType === NOTIFICATION_TYPES.PATIENT_REFERRAL &&
        group.channel === CHANNELS.EMAIL
    );

    // Convert user IDs back to user objects
    const subscribedUserIds = patientReferralEmailGroup?.userIds || [];
    const subscribedUsers = allUsers.filter((user) =>
      subscribedUserIds.includes(user.id)
    );

    return {
      patientReferralEmailUsers: subscribedUsers,
    };
  };

  const updateData = async (
    formData: NotificationSettingsFormData
  ): Promise<NotificationSettingsData> => {
    // Update patient referral email subscriptions
    await updateNotificationSubscriptions({
      notificationType: NOTIFICATION_TYPES.PATIENT_REFERRAL,
      channel: CHANNELS.EMAIL,
      userIds: formData.patientReferralEmailUsers.map((user) => user.id),
    });

    // Return the updated data
    return {
      patientReferralEmailUsers: formData.patientReferralEmailUsers,
    };
  };

  const renderView = ({ data }: { data: NotificationSettingsData }) => (
    <NotificationSettingsView data={data} />
  );

  const renderEdit = (props: {
    data: NotificationSettingsData;
    onSubmit: (formData: NotificationSettingsFormData) => void;
    submitRef: React.RefObject<(() => void) | null>;
  }) => <NotificationSettingsEdit {...props} />;

  return (
    <EditableBlock<NotificationSettingsData, NotificationSettingsFormData>
      title="Notification Settings"
      fetchData={fetchData}
      updateData={updateData}
      renderView={renderView}
      renderEdit={renderEdit}
    />
  );
};

export default NotificationSettingsBlock;
